import { useEffect, useCallback, useRef } from 'react'
import { useDebounce } from './useDebounce'
import {
  validatePlanName,
  NameValidationResponse,
} from '@/api/planNameValidation'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'

interface UsePlanNameValidationOptions {
  debounceMs?: number
  enabled?: boolean
}

interface UsePlanNameValidationReturn {
  isValidating: boolean
  isValid: boolean
  errorMessage: string | null
  validateNow: () => Promise<void>
}

/**
 * Hook for validating plan names with debouncing
 * Automatically validates when the name changes (after debounce)
 * Also provides a validateNow function for immediate validation
 */
export function usePlanNameValidation(
  name: string,
  options: UsePlanNameValidationOptions = {}
): UsePlanNameValidationReturn {
  const { debounceMs = 300, enabled = true } = options

  const { nameValidationStatus, nameValidationError, setNameValidationStatus } =
    usePlanWizardStore()

  // Debounce the name value
  const debouncedName = useDebounce(name, debounceMs)

  // Track if a validation is in progress
  const validationInProgressRef = useRef<string | null>(null)

  // Validation function
  const performValidation = useCallback(
    async (nameToValidate: string) => {
      const trimmedName = nameToValidate.trim()

      // If empty, reset to idle
      if (!trimmedName) {
        setNameValidationStatus('idle', null)
        return
      }

      // Mark as validating
      setNameValidationStatus('validating', null)
      validationInProgressRef.current = trimmedName

      try {
        const response: NameValidationResponse =
          await validatePlanName(trimmedName)

        // Only update if this is still the current validation
        if (validationInProgressRef.current === trimmedName) {
          if (response.is_valid) {
            setNameValidationStatus('valid', null)
          } else {
            setNameValidationStatus('invalid', response.error_message)
          }
        }
      } catch {
        // Only update if this is still the current validation
        if (validationInProgressRef.current === trimmedName) {
          setNameValidationStatus(
            'invalid',
            'Failed to validate name. Please try again.'
          )
        }
      }
    },
    [setNameValidationStatus]
  )

  // Validate immediately (bypassing debounce)
  const validateNow = useCallback(async () => {
    if (enabled && name.trim()) {
      await performValidation(name)
    }
  }, [enabled, name, performValidation])

  // Auto-validate when debounced name changes
  useEffect(() => {
    if (!enabled) {
      return
    }

    performValidation(debouncedName)
  }, [debouncedName, enabled, performValidation])

  // Reset validation status when name becomes empty
  useEffect(() => {
    if (!name.trim()) {
      setNameValidationStatus('idle', null)
    }
  }, [name, setNameValidationStatus])

  return {
    isValidating: nameValidationStatus === 'validating',
    isValid: nameValidationStatus === 'valid',
    errorMessage: nameValidationError,
    validateNow,
  }
}
